import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import portfolioService, { PerformancePosition } from '../../services/portfolio';

const COLORS = [
  '#2563eb', '#16a34a', '#dc2626', '#d97706',
  '#7c3aed', '#0891b2', '#be185d', '#15803d',
  '#92400e', '#1d4ed8',
];

const PORTFOLIO_COLOR = '#000537';
const GREY           = '#d4d4d8';

type DateRange = '1M' | '3M' | '6M' | '1Y' | 'YTD';

const DATE_RANGES: { key: DateRange; label: string }[] = [
  { key: '1M', label: '1M' },
  { key: '3M', label: '3M' },
  { key: '6M', label: '6M' },
  { key: '1Y', label: '1Y' },
  { key: 'YTD', label: 'YTD' },
];

const getFromDate = (range: DateRange): string => {
  const d = new Date();
  switch (range) {
    case '1M':  d.setMonth(d.getMonth() - 1); break;
    case '3M':  d.setMonth(d.getMonth() - 3); break;
    case '6M':  d.setMonth(d.getMonth() - 6); break;
    case '1Y':  d.setFullYear(d.getFullYear() - 1); break;
    case 'YTD': d.setMonth(0); d.setDate(1); break;
  }
  return d.toISOString().split('T')[0];
};

interface Props {
  portfolioSeries: { date: string; value: number }[];
  positions: PerformancePosition[];
}

const HoldingsBreakdownChart: React.FC<Props> = ({ portfolioSeries, positions }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [range, setRange] = useState<DateRange>('1Y');
  const from = useMemo(() => getFromDate(range), [range]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggle = (key: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const queries = useQueries({
    queries: positions.map(pos => ({
      queryKey:  ['benchmark-history', pos.ticker, from],
      queryFn:   () => portfolioService.getBenchmarkHistory(pos.ticker, from),
      staleTime: 15 * 60 * 1000,
      enabled:   !!pos.ticker,
    })),
  });

  const { chartData, colorMap, purchaseDates, hasData } = useMemo(() => {
    const filteredSeries = portfolioSeries.filter(p => p.date >= from);
    if (!filteredSeries.length) return { chartData: [], colorMap: {}, purchaseDates: {}, hasData: new Set<string>() };

    const basePortfolio = filteredSeries[0].value;

    const posMaps = new Map<string, Map<string, number>>();
    const dataSet = new Set<string>();

    positions.forEach((pos, i) => {
      const series = queries[i]?.data?.series ?? [];
      if (!series.length) return;
      const base = series[0].price;
      if (!base) return;
      dataSet.add(pos.ticker);
      const map = new Map<string, number>();
      let last = base;
      series.forEach(({ date, price }) => {
        if (price) last = price;
        map.set(date, (last / base) * 100);
      });
      posMaps.set(pos.ticker, map);
    });

    // Build chart rows aligned to filtered portfolio dates, carry-forward for each position
    const carried = new Map<string, number>();
    const data = filteredSeries.map(({ date, value }) => {
      const row: Record<string, any> = {
        date,
        _portfolio: (value / basePortfolio) * 100,
      };
      positions.forEach(pos => {
        const map = posMaps.get(pos.ticker);
        if (!map) return;
        const v = map.get(date);
        if (v != null) { carried.set(pos.ticker, v); row[pos.ticker] = v; }
        else if (carried.has(pos.ticker)) { row[pos.ticker] = carried.get(pos.ticker); }
      });
      return row;
    });

    const colorMap: Record<string, string> = {};
    positions.forEach((pos, i) => { colorMap[pos.ticker] = COLORS[i % COLORS.length]; });

    const dates = data.map(d => d.date);
    const purchaseDates: Record<string, string | null> = {};
    positions.forEach(pos => {
      purchaseDates[pos.ticker] = pos.purchase_date
        ? (dates.find(d => d >= pos.purchase_date!) ?? null)
        : null;
    });

    return { chartData: data, colorMap, purchaseDates, hasData: dataSet };
  }, [portfolioSeries, positions, queries]);

  if (!chartData.length) return null;

  // Render unselected lines first so selected ones appear on top in SVG z-order
  const sortedPositions = useMemo(() => [
    ...positions.filter(p => !selected.has(p.ticker)),
    ...positions.filter(p => selected.has(p.ticker)),
  ], [positions, selected]);

  const activeColor = (ticker: string) =>
    selected.has(ticker) ? (colorMap[ticker] ?? GREY) : GREY;

  return (
    <div className="card card-forest h-100">
      <div className="card-body p-3 p-md-4">
        {/* Row 1: title + range chips */}
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="card-title mb-0 fw-semibold text-forest-600">Holdings Performance</h5>
          <div className="d-flex gap-1">
            {DATE_RANGES.map(r => (
              <button
                key={r.key}
                onClick={() => setRange(r.key)}
                style={{
                  padding: '4px 10px', fontSize: '0.75rem', fontWeight: range === r.key ? 600 : 400,
                  borderRadius: '4px', border: 'none',
                  background: range === r.key ? '#000537' : 'transparent',
                  color: range === r.key ? '#fff' : '#71717a',
                  cursor: 'pointer',
                }}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* Row 2: highlight dropdown */}
        <div className="d-flex justify-content-end align-items-start mb-3">

          {/* Highlight dropdown */}
          <div ref={dropdownRef} style={{ position: 'relative', flexShrink: 0 }}>
            <button
              onClick={() => setDropdownOpen(o => !o)}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '4px 10px', borderRadius: 4, border: '1px solid #e4e4e7',
                background: '#fff', cursor: 'pointer', fontSize: '0.75rem',
                color: selected.size > 0 ? '#18181b' : '#71717a', fontWeight: selected.size > 0 ? 600 : 400,
              }}
            >
              Highlight{selected.size > 0 ? ` (${selected.size})` : ''} ▾
            </button>

            {dropdownOpen && (
              <div style={{
                position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 100,
                background: '#fff', border: '1px solid #e4e4e7', borderRadius: 6,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: 200, maxHeight: 260,
                overflowY: 'auto',
              }}>
                {selected.size > 0 && (
                  <button
                    onClick={() => setSelected(new Set())}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 12px', border: 'none', background: 'none', fontSize: '0.72rem', color: '#71717a', cursor: 'pointer', borderBottom: '1px solid #f4f4f5' }}
                  >
                    Clear all
                  </button>
                )}
                {positions.map((pos, i) => {
                  const isActive   = selected.has(pos.ticker);
                  const color      = colorMap[pos.ticker] ?? COLORS[i % COLORS.length];
                  const hasHistory = hasData.has(pos.ticker);
                  return (
                    <label
                      key={pos.ticker}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '6px 12px', cursor: hasHistory ? 'pointer' : 'default',
                        opacity: hasHistory ? 1 : 0.4,
                        background: isActive ? '#f9f9f9' : 'transparent',
                        fontSize: '0.78rem',
                      }}
                    >
                      <span style={{
                        width: 10, height: 10, borderRadius: '50%', flexShrink: 0, display: 'inline-block',
                        background: isActive ? color : GREY,
                      }} />
                      <span style={{ flex: 1, color: '#18181b' }}>{pos.name}</span>
                      <input
                        type="checkbox"
                        checked={isActive}
                        disabled={!hasHistory}
                        onChange={() => hasHistory && toggle(pos.ticker)}
                        style={{ accentColor: color, flexShrink: 0 }}
                      />
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>{/* row 2 */}

        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
            <XAxis
              dataKey="date"
              tickFormatter={d => {
                const dt = new Date(d);
                return `${dt.toLocaleString('default', { month: 'short' })} '${String(dt.getFullYear()).slice(2)}`;
              }}
              tick={{ fontSize: 11, fill: '#71717a' }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#71717a' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={v => v.toFixed(0)}
              width={36}
              domain={['auto', 'auto']}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                // Only show lines that have data + the portfolio line
                const rows = [...payload]
                  .filter(p => p.value != null)
                  .sort((a, b) => (b.value as number) - (a.value as number));
                return (
                  <div style={{ background: '#fff', border: '1px solid #f4f4f5', borderRadius: '8px', padding: '8px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: '0.75rem', maxHeight: 280, overflowY: 'auto', minWidth: 180 }}>
                    <div style={{ color: '#71717a', marginBottom: '4px' }}>{label}</div>
                    {rows.map(p => {
                      const val = p.value as number;
                      const isPortfolio = p.dataKey === '_portfolio';
                      const isSelected  = isPortfolio || selected.has(String(p.dataKey));
                      return (
                        <div key={String(p.dataKey)} className="d-flex align-items-center gap-2 mt-1">
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: isPortfolio ? PORTFOLIO_COLOR : (isSelected ? p.color : GREY), flexShrink: 0, display: 'inline-block' }} />
                          <span style={{ color: '#52525b' }}>{p.name}</span>
                          <span style={{ fontWeight: 600, marginLeft: 'auto', paddingLeft: 8, color: val >= 100 ? '#16a34a' : '#dc2626' }}>
                            {val.toFixed(1)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              }}
            />

            <ReferenceLine y={100} stroke="#e4e4e7" strokeDasharray="4 2" />

            {/* Portfolio — always prominent */}
            <Line
              type="monotone"
              dataKey="_portfolio"
              name="Portfolio"
              stroke={PORTFOLIO_COLOR}
              strokeWidth={2.5}
              dot={false}
            />

            {/* Position lines — unselected first, selected last for z-order */}
            {sortedPositions.map((pos, i) => {
              const color   = activeColor(pos.ticker);
              const marker  = purchaseDates[pos.ticker];
              const isActive = selected.has(pos.ticker);
              return (
                <Line
                  key={pos.ticker}
                  type="monotone"
                  dataKey={pos.ticker}
                  name={pos.name}
                  stroke={color}
                  strokeWidth={isActive ? 2 : 1}
                  opacity={isActive ? 1 : 0.6}
                  dot={(dp: any) => (
                    dp.payload?.date === marker
                      ? <circle key={`m-${pos.ticker}-${dp.index}`} cx={dp.cx} cy={dp.cy} r={isActive ? 4 : 3} fill={color} stroke="white" strokeWidth={2} />
                      : <circle key={`e-${pos.ticker}-${dp.index}`} cx={0} cy={0} r={0} fill="none" />
                  )}
                  connectNulls
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>

        {/* Legend — only shown when something is highlighted */}
        {selected.size > 0 && (
          <div className="d-flex align-items-center gap-2 mt-2" style={{ fontSize: '0.72rem', color: '#52525b' }}>
            <span style={{ width: 16, height: 2, background: PORTFOLIO_COLOR, display: 'inline-block', flexShrink: 0 }} />
            Portfolio
            {Array.from(selected).map(ticker => {
              const pos = positions.find(p => p.ticker === ticker);
              const color = colorMap[ticker] ?? GREY;
              return pos ? (
                <React.Fragment key={ticker}>
                  <span style={{ color: '#d4d4d8' }}>·</span>
                  <span style={{ width: 16, height: 2, background: color, display: 'inline-block', flexShrink: 0 }} />
                  {pos.name}
                </React.Fragment>
              ) : null;
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default HoldingsBreakdownChart;
